import { eComInstance } from "../utils/instance";
import {
  SubscriptionResponse,
  SubscriptionSource,
  SubscriptionUpsellDowngradeListResponse,
  PauseHistoryItem,
  PauseHistoryResponse,
} from "../types/model/subscriptions";
import {
  transformRestartSubscription,
  transformSubscriptions,
  transformUpsellDowngradeItem,
  transformPauseHistoryItem,
} from "../types/transformers/subscriptions";
import { SubscriptionPromotionsAndCountriesResponse } from "../types/dto/response/promotion";
import { transformSubscriptionPromotionsAndCountries } from "../types/transformers/promotion";
import { SubscriptionPromotionsAndCountries } from "../types/model/promotion";
import { PreviewPurchaseResponse } from "../types/dto/response/purchase";

export async function fetchSubscriptions(auth0Id: string) {
  const url = "/subscriptions";
  return eComInstance
    .get<SubscriptionResponse[]>(url, { params: { auth0Id } })
    .then((res) => res.map((sub) => transformSubscriptions(sub)));
}

export async function cancelSubscription(subscriptionId: string, auth0Id: string, source: string) {
  const url = `/subscriptions/${subscriptionId}/actions/cancel`;
  return eComInstance.post(url, null, {
    params: { auth0Id, source },
  });
}

export async function revokeSubscription(subscriptionId: string, auth0Id: string, source: string) {
  const url = `/subscriptions/${subscriptionId}/actions/revoke`;
  return eComInstance.post(url, null, {
    params: { auth0Id, source },
  });
}

export async function pauseSubscription(subscriptionId: string, auth0Id: string, pauseDays: number, source: string) {
  const url = `/subscriptions/${subscriptionId}/actions/pause`;
  return eComInstance.post(
    url,
    { paused_days: pauseDays },
    {
      params: { auth0Id, source },
    }
  );
}

export async function fetchDowngradePlanList(pid: string, source: SubscriptionSource, subscriptionId: string) {
  const url = `/variants/${pid}/downgrades?source=${source}&subscriptionId=${subscriptionId}`;
  return eComInstance
    .get<SubscriptionUpsellDowngradeListResponse[]>(url)
    .then((res) => res.map((item) => transformUpsellDowngradeItem(item)));
}

export async function fetchUpsellPlanList(pid: string, source: SubscriptionSource, subscriptionId: string) {
  const url = `/variants/${pid}/upsells?source=${source}&subscriptionId=${subscriptionId}`;
  return eComInstance
    .get<SubscriptionUpsellDowngradeListResponse[]>(url)
    .then((res) => res.map((item) => transformUpsellDowngradeItem(item)));
}

export async function submitSwitchPlan(
  auth0Id: string,
  email: string,
  subscriptionId: string,
  originPid: string,
  newPid: string
) {
  const url = `/subscriptions/${subscriptionId}/send-switch-plan-link`;
  return eComInstance.post(url, {
    auth0Id,
    email,
    original_pid: originPid,
    pid: newPid,
  });
}

export async function fetchSubscriptionPromotions(subscriptionId: string): Promise<SubscriptionPromotionsAndCountries> {
  const url = `/v2/subscriptions/${subscriptionId}/coupons`;
  return eComInstance
    .get<SubscriptionPromotionsAndCountriesResponse>(url)
    .then((res) => transformSubscriptionPromotionsAndCountries(res));
}

export async function submitApplyPromoCode(
  subscriptionId: string,
  promoCode: string,
  applyCountry: string,
  auth0Id: string
) {
  const url = `/v2/subscriptions/${subscriptionId}/actions/apply-coupon/${promoCode}`;
  return eComInstance.post(url, { auth0Id, applyCountry });
}

export async function resumeSubscription(subscriptionId: string, auth0Id: string, source: string) {
  const url = `/subscriptions/${subscriptionId}/actions/resume`;
  return eComInstance.post(url, null, {
    params: { auth0Id, source },
  });
}

export async function extendBillingDate(subscriptionId: string, extendDays: number, source: string) {
  const url = `/subscriptions/${subscriptionId}/actions/extend`;
  return eComInstance.post(url, { extend_days: extendDays, source });
}

export async function fetchRestartSubscription(subscriptionId: string) {
  const url = `v2/subscriptions/${subscriptionId}/actions/restart`;
  return eComInstance.get<PreviewPurchaseResponse>(url).then((res) => transformRestartSubscription(res));
}

export async function restartSubscription(subscriptionId: string) {
  const url = `v2/subscriptions/${subscriptionId}/actions/restart`;
  return eComInstance.post(url);
}

export async function fetchPauseHistory(subscriptionId: string): Promise<PauseHistoryItem[]> {
  const url = `v2/subscriptions/${subscriptionId}/pause-history`;
  return eComInstance.get<PauseHistoryResponse[]>(url).then((res) => res.map(transformPauseHistoryItem));
}
